import { create } from 'zustand';
import { initializeEconomyFromDevSettings } from './economyStore';

export type ScreenType =
  | 'menu'
  | 'loading'
  | 'game'
  | 'gameOver'
  | 'settings'
  | 'devAdmin'
  | 'devMenu'
  | 'mapMaker';

type SettingsContext = 'player' | 'dev';

interface GameState {
  currentScreen: ScreenType;
  isGameOver: boolean;
  isPaused: boolean;
  showInGameSettings: boolean;
  settingsContext: SettingsContext;
  gameTime: number;
  setScreen: (screen: ScreenType) => void;
  startGame: () => void;
  returnToMenu: () => void;
  triggerGameOver: () => void;
  pauseGame: () => void;
  resumeGame: () => void;
  showInGameSettingsMenu: () => void;
  showDevSettingsMenu: () => void;
  hideInGameSettingsMenu: () => void;
  updateGameTime: (deltaTime: number) => void;
  resetGameTime: () => void;
}

export const useGameStateStore = create<GameState>((set, get) => ({
  currentScreen: 'menu',
  isGameOver: false,
  isPaused: false,
  showInGameSettings: false,
  settingsContext: 'player',
  gameTime: 0,
  setScreen: (screen: ScreenType) => set({ currentScreen: screen }),
  startGame: () => {
    // Initialize economy based on current dev settings when a new game starts
    initializeEconomyFromDevSettings();

    // Transition: menu -> loading
    // The game screen will be shown once assets are loaded (handled in App.tsx)
    set({
      currentScreen: 'loading',
      isGameOver: false,
      isPaused: false,
      showInGameSettings: false,
      settingsContext: 'player',
      gameTime: 0,
    });
  },
  returnToMenu: () =>
    set({
      currentScreen: 'menu',
      isGameOver: false,
      isPaused: false,
      showInGameSettings: false,
      settingsContext: 'player',
      gameTime: 0,
    }),
  triggerGameOver: () => {
    set(() => {
      return {
        isGameOver: true,
        currentScreen: 'gameOver',
        isPaused: false,
        showInGameSettings: false,
        settingsContext: 'player',
      };
    });
  },
  pauseGame: () => set({ isPaused: true }),
  resumeGame: () => set({ isPaused: false, showInGameSettings: false, settingsContext: 'player' }),
  showInGameSettingsMenu: () => set({ showInGameSettings: true, settingsContext: 'player' }),
  showDevSettingsMenu: () => set({ showInGameSettings: true, settingsContext: 'dev' }),
  hideInGameSettingsMenu: () => set({ showInGameSettings: false, settingsContext: 'player' }),
  updateGameTime: (deltaTime: number) => {
    const { isPaused, isGameOver } = get();
    if (!isPaused && !isGameOver) {
      set((state) => ({ gameTime: state.gameTime + deltaTime }));
    }
  },
  resetGameTime: () => set({ gameTime: 0 }),
}));
